"use client"

// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025 Collab Digital Twins

// Dependencies
import { Languages } from 'lucide-react'
import { useRouter } from 'next/navigation'
import { useTranslations, useLocale } from 'next-intl'
import * as React from "react";

// Shadcn Components
import { Button } from '../components/ui/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/DropdownMenu'

// Icons
import { Language } from '../types/dbTypes';
import { switchLanguage } from '../utils/utils';

const LANGUAGE_OPTIONS: { lang: Language; labelKey: 'english' | 'french' | 'spanish' }[] = [
    { lang: Language.En, labelKey: 'english' },
    { lang: Language.Fr, labelKey: 'french' },
    { lang: Language.Es, labelKey: 'spanish' },
]

interface LanguageSwitchProps {
    showLabel?: boolean;
    variant?: 'ghost' | 'outline';
    languages?: Language[];
}

export default function LanguageSwitch({ showLabel, variant='ghost', languages }: LanguageSwitchProps) {

    // i18n
    const locale = useLocale()
    const router = useRouter()
    const [_, setCurrentLocale] = React.useState(locale)
    const t = useTranslations('LanguageSwitch')

    const effectiveLanguages = languages?.length ? languages : [Language.En]
    const options = effectiveLanguages
        .map(lang => LANGUAGE_OPTIONS.find(o => o.lang === lang))
        .filter(Boolean)

    React.useEffect(() => {
        if (!effectiveLanguages.includes(locale as Language)) {
            void switchLanguage(effectiveLanguages[0], setCurrentLocale, router)
        }
    }, [])

    if (options.length <= 1) return null

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
            <Button
                variant={variant}
                size={showLabel ? "default" : "icon"}
                className={`text-xs ${showLabel ? 'w-full p-2 justify-start h-auto' : ''}`}
                title={t('tooltip')}
            >
                <Languages />
                {showLabel &&
                <span>{t('languageLabel')}</span>
                }
            </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
            {options.map(({ lang, labelKey }) => (
                <DropdownMenuItem
                    key={lang}
                    onClick={() => { void switchLanguage(lang, setCurrentLocale, router) }}
                >
                    {t(labelKey)}
                </DropdownMenuItem>
            ))}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}